<template>
  <div ref="startscreenDiv" class="knw_widget_startscreen">
    <div class="avatar-widget">
      <Avatar class="avatar-widget__avatar" width="100px" height="100px" :image="getDefaultAvatarUrl" />
      <div class="avatar-widget__wave">
        👋
      </div>
    </div>

    <h2>{{ startScreen.greeting }}</h2>

    <div class="body">
      <p>{{ startScreen.text }}</p>

      <ul v-if="hasStartscreenLinks">
        <li v-for="link in startScreen.links" :key="link.title">
          <a :href="link.href" target="_blank">{{ link.title }}</a>
        </li>
      </ul>
      <button :style="getButtonColor" @click="acceptStartscreen">
        {{ startScreen.buttonLabel }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Avatar from '@/components/common/Avatar.vue';
import { useWidgetStore } from '@/store/widget';

import { computed, ref, toRef } from 'vue';
import { getColorContrastRatio, parseColor } from '@/common/colors';

import { useMoinBot } from '@/services/bot/botv2';
import { useThemeStore } from '@/store/theme';
import type { Startscreen } from '@/types';

const bot = useMoinBot();

const getDefaultAvatarUrl = computed(() => bot.getDefaultAvatarUrl.value)

const startscreenDiv = ref(null);
const widgetStore = useWidgetStore();
const startScreen = toRef<Startscreen>(widgetStore.getStartscreen);
const theme = useThemeStore();

const hasStartscreenLinks = computed(() => {
  return startScreen.value.links && startScreen.value.links.length >= 1;
});

const getButtonColor = computed(() => {
  const textColor = parseColor(
    theme.getThemeVariable('--moin-widget-header-textcolor')
  );
  const black = getColorContrastRatio(textColor.rgb, parseColor('black').rgb);
  const white = getColorContrastRatio(textColor.rgb, parseColor('white').rgb);
  return { color: black > white ? 'black' : 'white' };
});

const acceptStartscreen = () => {
  widgetStore.acceptStartscreen();
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.knw_widget_startscreen {
  background-color: var(--kw-widget-header-background);
  color: var(--kw-widget-header-textcolor);
  height: 100%;
  padding: 15px 15px 25px 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .avatar-widget {
    display: flex;
    align-items: flex-start;
    // center on the avatar so add the width of the bubble and wave to the left
    padding-left: 52.5px;

    &__avatar {
      margin-top: 10px;
    }

    &__wave {
      background: white;
      padding: 7px 10px;
      border-radius: 5px 5px 5px 0;
      display: flex;
      position: relative;
      margin-left: 15px;
      font-size: 1.8rem;

      &:before {
        content: '';
        width: 0;
        height: 0;
        border-width: 0 0 10px 10px;
        border-color: transparent transparent #fff transparent;
        border-style: solid;
        position: absolute;
        left: -10px;
        bottom: 0;
      }
    }
  }

  h2 {
    margin-top: 20px;
    font-weight: normal;
    font-size: 1.9rem;
    margin-bottom: 24px;

    @media (min-width: 501px) {
      margin-bottom: 54px;
    }
  }

  .body {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;

    line-height: 1.4;
    font-size: 1.6rem;

    @media (min-width: 501px) {
      padding-left: 50px;
      padding-right: 50px;
    }


    @media (min-width: 501px) {
      font-size: 1.7rem;
    }

    ul {
      margin: 0;
      padding: 0;

      li {
        list-style: none;
        margin-bottom: 12px;
      }
    }

    a {
      color: var(--kw-widget-header-textcolor);
    }

    button {
      background-color: var(--kw-widget-header-textcolor);
      color: var(--kw-widget-header-background);
      border-radius: 25px;
      border: none;
      padding: 10px 20px;
      margin-left: auto;
      display: block;
      font-weight: bold;

      &:hover {
        cursor: pointer;
      }
    }
  }
}
</style>
